/* eslint-disable space-before-blocks */
import React from 'react';

import {
  nonEmptyValidator,
  numberOnlyValidator,
  minNumberValidator,
  maxNumberValidator,
} from 'src/validators';
import {
  NORMALIZATION_VOLUME_MIN_VALUE,
  SMALL_WELL_OPTIONS,
  BIG_WELL_OPTIONS,
  PLATE_SIZES,
  CONCENTRATION,
  NORMALIZATION_CONCENTRATION_UM_MIN_VALUE,
  NORMALIZATION_CONCENTRATION_UM_MAX_VALUE,
  NORMALIZATION_CONCENTRATION_UG_UL_MIN_VALUE,
  NORMALIZATION_CONCENTRATION_UG_UL_MAX_VALUE,
  UNITS,
} from 'src/constants/forms';
import { Message } from 'src/core/components';

import messages from '../messages';

export const REG_EXP_FLOAT =
  /^\s*^(?!(.*\.00)|(00\..*)|-0[1-9].*$)^-?(?=.*[1-9])\d*(?:\.\d{1,9})?\s*$/;

export const getVolumeMaxValue = (plateSize = '', well = '') => {
  if (plateSize === PLATE_SIZES.SIZE_96) {
    switch (well) {
      case SMALL_WELL_OPTIONS.SMALL.type:
        return SMALL_WELL_OPTIONS.SMALL.maxValue;
      case SMALL_WELL_OPTIONS.EXTRA_SMALL.type:
        return SMALL_WELL_OPTIONS.EXTRA_SMALL.maxValue;
      case SMALL_WELL_OPTIONS.MEDIUM.type:
        return SMALL_WELL_OPTIONS.MEDIUM.maxValue;
      case SMALL_WELL_OPTIONS.LARGE.type:
        return SMALL_WELL_OPTIONS.LARGE.maxValue;
      case SMALL_WELL_OPTIONS.EXTRA_LARGE.type:
        return SMALL_WELL_OPTIONS.EXTRA_LARGE.maxValue;
      case SMALL_WELL_OPTIONS.DOUBLE_EXTRA_LARGE.type:
        return SMALL_WELL_OPTIONS.DOUBLE_EXTRA_LARGE.maxValue;
      default:
        return SMALL_WELL_OPTIONS.FULL_LARGE.maxValue;
    }
  } else {
    switch (well) {
      case BIG_WELL_OPTIONS.SMALL.type:
        return BIG_WELL_OPTIONS.SMALL.maxValue;
      default:
        return BIG_WELL_OPTIONS.MEDIUM.maxValue;
    }
  }
};

export const floatOnlyValidator = () => ({
  validator: (value) => {
    if (!REG_EXP_FLOAT.test(value)) {
      return 'error';
    }

    return 'success';
  },
  errorMessage: (
    <Message message={messages.noNrNormalizationConcentrationError} />
  ),
});

export const normalizationAndAliquotsValidator = (
  minimumQuantity,
  maximumQuantity,
  paramKey,
  userInput,
  errorMessage = {},
  isFloat = false
) => ({
  validator: (value) => {
    const parser = isFloat ? parseFloat : parseInt;

    return parser(value, 10) <= maximumQuantity &&
      parser(value, 10) >= minimumQuantity ? 'success' : 'error';
  },
  errorMessage: (
    <Message
      message={{
        ...errorMessage,
        values: { [paramKey]: userInput, minimumQuantity, maximumQuantity },
      }}
    />
  ),
});

const getConcentrationValidators = (unitType = '') => {
  if (unitType === UNITS.concentration[0].value) {
    return [
      minNumberValidator(
        NORMALIZATION_CONCENTRATION_UM_MIN_VALUE,
        messages.minNrNormalizationConcentrationUmError,
        true
      ),
      maxNumberValidator(
        NORMALIZATION_CONCENTRATION_UM_MAX_VALUE,
        messages.maxNrNormalizationConcentrationUmError,
        true
      ),
      floatOnlyValidator(),
    ];
  }

  return [
    minNumberValidator(
      NORMALIZATION_CONCENTRATION_UG_UL_MIN_VALUE,
      messages.minNrNormalizationConcentrationUgUlError,
      true
    ),
    maxNumberValidator(
      NORMALIZATION_CONCENTRATION_UG_UL_MAX_VALUE,
      messages.maxNrNormalizationConcentrationUgUlError,
      true
    ),
    floatOnlyValidator(),
  ];
};

export const getInputValidators = (
  formData,
  fieldName,
  dropdownName,
  plateSizeDetails = {}
) => {
  if (fieldName === CONCENTRATION) {
    return [
      nonEmptyValidator(messages.emptyNormalizationConcentrationInputError),
      ...getConcentrationValidators(
        formData[dropdownName]?.value,
        formData[fieldName]?.value
      ),
    ];
  }

  return [
    nonEmptyValidator(messages.emptyNormalizationVolumeInputError),
    numberOnlyValidator(messages.noNrNormalizationVolumeError),
    minNumberValidator(
      NORMALIZATION_VOLUME_MIN_VALUE,
      messages.minNrNormalizationVolumeError
    ),
    maxNumberValidator(
      getVolumeMaxValue(
        plateSizeDetails.plateSize?.value,
        plateSizeDetails.wellVolume?.value
      ),
      messages.maxNrNormalizationVolumeError
    ),
  ];
};
